/* D2 — HUB NOAM 3+1 : point d'entrée post-login.
   NOAM -> 3 Systems opérationnels (souscription tenant) -> 1 Platform partagée.
   Additif : aucune page existante modifiée. Le gating serveur reste la vérité. */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Bot, Database, Lock, MessageSquare, Network, ShieldCheck, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { SYSTEM_DEFS, useSystems } from "@/lib/systems";

const PLATFORM_PILLARS = [
  { label: "Ontology", icon: Network, to: "/app/ontologie" },
  { label: "Intelligence", icon: Sparkles, to: "/app/intelligence" },
  { label: "AI & Automation", icon: Bot, to: "/app/settings/agents" },
  { label: "Trust & Security", icon: ShieldCheck, to: "/app/users" },
  { label: "Communication & Network", icon: MessageSquare, to: "/app/chat-interne" },
  { label: "Data & Infrastructure", icon: Database, to: "/app/documents" },
];

function useLightKpis(systems) {
  const { t } = useTranslation();
  const [kpis, setKpis] = useState({});
  useEffect(() => {
    let cancelled = false;
    const put = (id, text) => !cancelled && setKpis((k) => ({ ...k, [id]: text }));
    if (systems.includes("deal"))
      api.get("/workspace/deals")
        .then((r) => put("deal", t("saas.axp.hub_kpi_deals", { n: r.data?.stats?.count ?? 0 })))
        .catch(() => put("deal", "—"));
    if (systems.includes("financial_markets"))
      api.get("/workspace/brvm/companies")
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : r.data?.items || r.data?.companies || [];
          put("financial_markets", t("saas.axp.hub_kpi_cos", { n: list.length }));
        })
        .catch(() => put("financial_markets", "—"));
    if (systems.includes("governance"))
      api.get("/workspace/grc/summary")
        .then((r) => put("governance", t("saas.axp.hub_kpi_grc", { r: r.data?.risks ?? 0, c: r.data?.controls ?? 0 })))
        .catch(() => put("governance", "—"));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systems]);
  return kpis;
}

function SystemCard({ def, enabled, kpi, index }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div
      onClick={() => enabled && navigate(def.landing)}
      className={`group relative border rounded-xl p-8 lg:p-10 flex flex-col gap-4 transition-colors transition-transform duration-200 ease-out hub-fade-up ${
        enabled
          ? "bg-[#111113] border-white/10 hover:border-white/25 hover:bg-[#161618] hover:-translate-y-0.5 cursor-pointer"
          : "bg-[#0D0D0F] border-white/[0.06]"
      }`}
      style={{ animationDelay: `${120 + index * 110}ms` }}
      data-testid={`system-card-${def.id}`}
    >
      <div className="flex items-start justify-between">
        <div
          className="w-11 h-11 rounded-lg border flex items-center justify-center"
          style={{
            borderColor: enabled ? `${def.accent}66` : "rgba(255,255,255,0.08)",
            background: enabled ? `${def.accent}1a` : "rgba(255,255,255,0.03)",
          }}
        >
          <def.icon className="w-5 h-5" style={{ color: enabled ? def.accent : "#52525b" }} />
        </div>
        {!enabled && (
          <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.18em] text-zinc-500 border border-white/10 rounded-full px-2.5 py-1">
            <Lock className="w-3 h-3" /> {t("saas.axp.hub_not_sub")}
          </span>
        )}
      </div>
      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1.5">System</div>
        <h3 className={`noam-display text-xl lg:text-2xl font-bold tracking-tight ${enabled ? "text-white" : "text-zinc-400"}`}>
          {def.label}
        </h3>
      </div>
      <p className="text-sm text-white/55 leading-relaxed flex-1">{t(`saas.axp.sysdesc_${def.id}`, { defaultValue: def.description })}</p>
      {enabled ? (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-zinc-400" data-testid={`system-kpi-${def.id}`}>{kpi || "…"}</span>
          <span
            className="inline-flex items-center gap-1.5 text-sm font-medium text-white px-4 py-2 rounded-md transition-colors"
            style={{ background: def.accent }}
            data-testid={`system-enter-${def.id}`}
          >
            {t("saas.axp.hub_enter")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-zinc-600">{t("saas.axp.hub_no_data")}</span>
          <span
            className="inline-flex items-center gap-1.5 text-sm text-zinc-300 px-4 py-2 rounded-md border border-white/10 hover:bg-white/5 transition-colors cursor-default"
            data-testid={`system-discover-${def.id}`}
            title={t("saas.axp.hub_activate")}
          >
            <Sparkles className="w-4 h-4" /> {t("saas.axp.hub_discover")}
          </span>
        </div>
      )}
    </div>
  );
}

export default function Hub() {
  const { t } = useTranslation();
  const { systems } = useSystems();
  const kpis = useLightKpis(systems);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="noam-hub">
      <style>{`
        @keyframes hubFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .hub-fade-up { opacity: 0; animation: hubFadeUp .5s ease-out forwards; }
      `}</style>

      {/* Hero typographique */}
      <div className="mb-12 lg:mb-16 hub-fade-up" style={{ animationDelay: "0ms" }}>
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#378ADD] mb-4">Noam Platform</div>
        <h1 className="noam-display text-4xl sm:text-5xl font-bold tracking-tighter text-white max-w-3xl">
          {t("saas.axp.hub_title1")}<br />{t("saas.axp.hub_title2")}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-500">
          {t("saas.axp.hub_verbs").split("·").map((v, i) => (
            <span key={v} className="inline-flex items-center gap-3">
              {i > 0 && <span className="opacity-40">·</span>}
              <span className="text-white/60">{v}</span>
            </span>
          ))}
        </div>
      </div>

      {/* 3 Systems */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-10 lg:mb-14">
        {SYSTEM_DEFS.map((def, i) => (
          <SystemCard key={def.id} def={def} enabled={systems.includes(def.id)} kpi={kpis[def.id]} index={i} />
        ))}
      </div>

      {/* Platform partagée */}
      <div
        className="border border-white/10 rounded-xl bg-[#0C1A2E]/40 p-8 lg:p-10 hub-fade-up"
        style={{ animationDelay: "480ms" }}
        data-testid="platform-band"
      >
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#B5D4F4]/60 mb-1.5">{t("saas.axp.hub_transverse")}</div>
            <h2 className="noam-display text-lg font-bold tracking-tight text-white">{t("saas.axp.hub_shared")}</h2>
          </div>
          <span className="text-xs text-zinc-500">{t("saas.axp.hub_always")}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {PLATFORM_PILLARS.map((p) => (
            <Link
              key={p.label}
              to={p.to}
              className="border border-white/[0.06] rounded-lg px-4 py-4 flex flex-col gap-2.5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/15 transition-colors"
              data-testid={`platform-pillar-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <p.icon className="w-4 h-4 text-[#B5D4F4]/70" />
              <span className="text-xs text-white/70 leading-snug">{p.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
